import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';

interface NightModeContextType {
  isNightMode: boolean;
  toggleNightMode: () => void;
}

const NightModeContext = createContext<NightModeContextType | undefined>(undefined);

export const useNightMode = () => {
  const context = useContext(NightModeContext);
  if (context === undefined) {
    throw new Error('useNightMode must be used within a NightModeProvider');
  }
  return context;
};

interface NightModeProviderProps {
  children: ReactNode;
}

export const NightModeProvider: React.FC<NightModeProviderProps> = ({ children }) => {
  const [isNightMode, setIsNightMode] = useState(true);

  // Initialize night mode state on component mount (defaulting to dark mode)
  useEffect(() => {
    const savedNightMode = localStorage.getItem('gmtNightMode');
    const htmlElement = document.documentElement;
    
    if (savedNightMode === 'false') {
      htmlElement.classList.remove('night-mode');
      setIsNightMode(false);
    } else {
      htmlElement.classList.add('night-mode');
      setIsNightMode(true);
      if (!savedNightMode) {
        localStorage.setItem('gmtNightMode', 'true');
      }
    }
  }, []);

  // Night mode toggle function
  const toggleNightMode = () => {
    const htmlElement = document.documentElement;
    const newNightModeState = !isNightMode;
    
    if (newNightModeState) {
      htmlElement.classList.add('night-mode');
      localStorage.setItem('gmtNightMode', 'true');
    } else {
      htmlElement.classList.remove('night-mode');
      localStorage.removeItem('gmtNightMode');
    }
    
    setIsNightMode(newNightModeState);
  };

  return (
    <NightModeContext.Provider value={{ isNightMode, toggleNightMode }}>
      {children}
    </NightModeContext.Provider>
  );
}; 